"use client";
import { useState } from "react";
import { CheckCircle, PhoneIcon, InfoIcon } from "lucide-react";
import { Popover, Switch, Tooltip } from "antd";
import ExpandedReportIcon from "../../../app/inspection/icons/ExpandedReportIcon";
import CustomDateRangeSelector from "../../ui/shared/CustomDateRangeSelector";
import { format } from "date-fns";
import { formSchema } from "./TripAuditInspection"; // adjust path
import z from "zod";
import { useFormContext } from "react-hook-form";
import FBModal from "../../ui/shared/FBModal";
import { AxiosErrorResponse } from "../../../interface/AxiosErrorResponse";
import toast from "react-hot-toast";
import axios from "axios";
import { pricingPlans } from "../../../static_data/data";
import PreviousButtonIcon from "../icons/PreviousButtonIcon";
import { useTripAudit } from "../../../contexts/TripAuditContext";
import LoadingButtonAnimation from "../../ui/shared/ButtonLoadingAnimation";
import DriverPermitCardSkeleton from "../../../app/features/driver-license/components/DriverPermitCardSkeleton";
import CollapseIcon from "../../../app/inspection/[token]/result/Icons/CollapseIcon";
import apiConfig from "../../../lib/config";
const stampAccessPrices = {
  "90": 5,
  "180": 10,
  "365": 15,
} as const;
type FormData = z.infer<typeof formSchema>;

export default function InsuranceProtectionCard() {
  const [showDriveInfo, setShowDriveInfo] = useState(true);
  const [editDurations, setEditDurations] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { watch, getValues, setValue, handleSubmit, reset } =
    useFormContext<FormData>();
  const { setShowInsurannceCover } = useTripAudit();
  const inspectionPlan = watch("inspection.plan");
  const durations = watch("inspection.access_limit_time");
  const planName = watch("inspection.plan") || "";
  const licenseVerification = watch("inspection.license_verification") ?? false;

  const toggleLicenseVerification = (checked: boolean) => {
    setValue("inspection.license_verification", checked, {
      shouldValidate: true,
    });
  };
  // calculate the price
  const accessPrice =
    stampAccessPrices[durations as keyof typeof stampAccessPrices] ?? 0;
  const selectedPlan = pricingPlans.plans.find(
    (plan) => plan.value === inspectionPlan,
  );
  const LICENSE_VERIFICATION_PRICE = 4.99;
  const planPrice = selectedPlan?.price ?? 0;
  const totalPrice =
    planPrice +
    accessPrice +
    (licenseVerification ? LICENSE_VERIFICATION_PRICE : 0);
  const toggleDuration = (duration: "90" | "180" | "365") => {
    const selectedDuration = getValues("inspection.access_limit_time");
    setValue(
      "inspection.access_limit_time",
      selectedDuration === duration ? undefined : duration,
      { shouldValidate: true },
    );
  };

  const formData = watch();
  // Add this state at the top of your component
  const [agreeTerms, setAgreeTerms] = useState(false);

  // const dailyPrice = 8.42;
  // const totalPrice = 42.08;
  // const originalTotal = 49.5;

  // const handleToggle = (checked: boolean) => {
  //   setIsIncluded(checked);
  // };
  const handleDateChange = (start: Date | null, end: Date | null) => {
    if (start) {
      const startStr = format(start, "yyyy-MM-dd");
      setValue("trip.startDate", startStr, { shouldValidate: true });
    }
    if (end) {
      const endStr = format(end, "yyyy-MM-dd");
      setValue("trip.endDate", endStr, { shouldValidate: true });
    }
  };
  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const payload = {
        car_make: data.vehicle.make,
        car_model: data.vehicle.model,
        car_year: Number(data.vehicle.year),
        fuel_type: data.vehicle.fuelType,
        exterior_color: data.vehicle.color || null,
        car_owner_name: data.owner.fullName,
        car_owner_email: data.owner.email,
        car_owner_phone: data.owner.phone,
        car_owner_address: data.owner.address || null,
        driver_name: data.driver.fullName,
        driver_email: data.driver.email,
        driver_phone: data.driver.phone,
        driving_license: data.driver.license || null,
        driver_address: data.driver.address || null,
        inspection_type:
          data.inspection.plan?.toLowerCase() === "single" ? "single" : "dual",
        access_limit_time: Number(data.inspection.access_limit_time || "30"),
        payment_amount: planPrice,
        increment_payment_amount: accessPrice ?? 0,
        // insurance_start_date: data.trip?.startDate || null,
        // insurance_end_date: data.trip?.endDate || null,
        is_driver_permit: licenseVerification,
        terms_and_conditions: agreeTerms,
        // coverage_amount: isIncluded ? 5000 : 0,
        marketing: agreeMarketing,
      };
      const orderResponse = await axios.post(
        `${apiConfig.api.baseUrl}/create_order`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );
      const tripId = orderResponse.data.trip_id;
      const customerEmail = data?.owner?.email ?? "";
      const createSessionData = {
        // success_url: "https://fleetblox.com/trip-audit/payment-success",
        success_url: `https://drivestamp.com/payment-success?email=${customerEmail}`,
        cancel_url: "https://drivestamp.com/payment-failed",
      };
      // Create payment intent
      const paymentResponse = await axios.post(
        `${apiConfig.api.baseUrl}/create_checkout_session?trip_id=${tripId}`,
        createSessionData,
      );
      if (paymentResponse?.data?.checkout_url) {
        reset();
        // router.push(paymentResponse.data.checkout_url);
        window.location.assign(paymentResponse.data.checkout_url);
      }
    } catch (error) {
      const axiosError = error as AxiosErrorResponse;
      const errorMessage =
        axiosError?.response?.data?.error?.message ||
        "An unexpected error occurred";
      toast.error(
        `${errorMessage || "Failed to create order. Please try again."}`,
      );
    } finally {
      setIsLoading(false);
    }
  };
  const scrollToTripwiseSection = () => {
    setTimeout(() => {
      document.getElementById("tripwise-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  };
  return (
    <div className="max-w-6xl mx-auto">
      <button
        onClick={() => {
          setShowInsurannceCover(false);
          scrollToTripwiseSection();
        }}
        className="flex items-center gap-2 mb-8 cursor-pointer"
      >
        <PreviousButtonIcon />
        <span className="text-[14px] font-semibold text-[#999]">Previous</span>
      </button>
      <div className=" bg-white rounded-md shadow-2xl overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="bg-[#FAFAFF] px-5 py-5 text-[#303030]">
          <h1 className="text-3xl font-bold">Your order info</h1>
          <p className="text-[#6F6464] mt-2">
            Review your order and proceed with the payment
          </p>
        </div>
        <div className="grid lg:grid-cols-5">
          {/* Left Column - Packages + Coverage Details */}
          <div className="lg:col-span-3 p-5 lg:border-r border-[#DDD] min-h-[650px]">
            <div className="border border-[#DDD] rounded-md  p-3 mb-5">
              <button
                onClick={() => setShowDriveInfo(!showDriveInfo)}
                className="w-full flex items-center justify-between "
              >
                <div className=" mb-5">
                  <h3 className="text-[#303030] text-[20px] font-semibold text-left">
                    {formData.vehicle?.make} {formData.vehicle?.model}{" "}
                    {formData.vehicle?.year}
                  </h3>
                  <div className="flex space-x-2.5 items-center">
                    <p className="text-[#6F6464] text-[12px] font-medium leading-4">
                      VIN- {formData.vehicle?.vin || "N/A"}
                    </p>
                    <div className="bg-[#999] w-px h-4 opacity-30"></div>
                    <p className="text-[#6F6464] text-[12px] font-medium leading-4">
                      License-{formData.driver?.license || "N/A"}
                    </p>
                  </div>
                </div>
                {showDriveInfo ? <ExpandedReportIcon /> : <CollapseIcon />}
              </button>
              {showDriveInfo && (
                <div>
                  {/* driver info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="border border-[#DFDFDF] bg-[#FAFAFA] rounded-[10px] p-4 mb-2.5">
                      <h3 className="text-[#333] text-[12px] leading-5 font-openSans font-bold mb-3">
                        Driver info
                      </h3>

                      <div className="flex items-center justify-between mb-3">
                        {/* Name */}
                        <div>
                          <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                            {formData.driver?.fullName || "N/A"}
                          </p>
                          <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                            Name
                          </p>
                        </div>
                        {/* License */}
                        <div>
                          <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                            DL-{formData.driver?.license || "N/A"}
                          </p>
                          <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                            Driver’s license
                          </p>
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div className="flex items-center justify-between pt-3 border-t border-[#DFDFDF]">
                        <div>
                          <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                            {formData.driver?.phone || "N/A"}
                          </p>
                          <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                            Phone number
                          </p>
                        </div>
                        {formData.driver?.phone && (
                          <a
                            href={`tel:${formData.driver.phone}`}
                            className="text-[#2D65F2]"
                          >
                            <PhoneIcon color="#2D65F2" size={16} />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="border border-[#B8CBFC] bg-[#F5F9FC] rounded-[10px] p-4 mb-2.5">
                      <h3 className="text-[#333] text-[12px] leading-5 font-openSans font-bold mb-3">
                        Owner info
                      </h3>

                      <div className="flex items-center justify-between mb-3">
                        {/* Name */}
                        <div>
                          <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                            {formData.owner?.fullName || "N/A"}
                          </p>
                          <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                            Name
                          </p>
                        </div>
                        {/* License */}
                        <div>
                          <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                            {/* DL-{driving_license || "Missing"} */}
                            DL-{formData.driver?.license || "N/A"}
                          </p>
                          <p className="text-[12px] font-medium leading-4 text-[#6F6464] font-inter">
                            Driver’s license
                          </p>
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div className="flex items-center justify-between pt-3 border-t border-[#DFDFDF]">
                        <div>
                          <p className="text-[14px] font-medium leading-[18px] text-[#333]">
                            {formData.owner?.phone || "N/A"}
                          </p>
                          <p className="text-[12px] font-medium leading-4 text-[#6F6464]">
                            Phone number
                          </p>
                        </div>
                        {formData.owner?.phone && (
                          <a
                            href={`tel:${formData.owner.phone}`}
                            className="text-[#2D65F2]"
                          >
                            <PhoneIcon color="#2D65F2" size={16} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Package Selection */}
            <h2 className="text-xl font-semibold text-[#04082C] mb-5">
              Selected Stamp Plan
            </h2>
            <div
              className={`p-5 mb-5 rounded-md border flex justify-between  cursor-pointer border-[#B8CBFC] bg-[#F5F9FC]  transition-all `}
            >
              <div>
                <h3 className="font-bold text-[#04082C]">
                  {planName === "Single"
                    ? "Drive Log"
                    : planName === "Dual"
                      ? "Drive Audit"
                      : ""}
                </h3>
                <p className="text-sm text-gray-500">
                  {" "}
                  {planName === "Single"
                    ? "Pre drive"
                    : planName === "Dual"
                      ? "Post drive"
                      : ""}
                </p>
              </div>
              <p className="text-2xl font-bold text-[#04082C] mt-3">
                ${planPrice.toFixed(2)}
              </p>
            </div>
            <div>
              {/* Interactive Stamp Access - Duration Cards */}
              <div className="flex items-center gap-2 mb-5">
                <h2 className="text-xl font-semibold text-[#04082C] ">
                  Interactive Stamp Access
                </h2>
                <Tooltip
                  placement="top"
                  title={
                    "Select the duration for which you want to access your stamp.by default, it is set to 30 days."
                  }
                >
                  <div className="flex  items-center">
                    <InfoIcon fill="#02636F" size={24} />
                  </div>
                </Tooltip>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {/* Card 1: 90 Days */}
                <div
                  key="90"
                  className={`border rounded-xl p-5 cursor-pointer transition-all hover:shadow-md ${
                    durations === "90"
                      ? "border-[#2D65F2] bg-[#F5F9FF]"
                      : "border-[#DFDFDF] hover:border-gray-300"
                  }`}
                  onClick={(event) => {
                    if (event.target instanceof HTMLInputElement) return;
                    toggleDuration("90");
                  }}
                >
                  <div className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      value="90"
                      checked={durations === "90"}
                      onChange={() => toggleDuration("90")}
                      className="mt-1 accent-[#2D65F2]"
                    />
                    <div className="flex-1">
                      <p
                        className={`text-[18px] font-bold font-openSans ${
                          durations === "90"
                            ? "text-[#151515]"
                            : "text-[#6F6464]"
                        }`}
                      >
                        90 Days
                      </p>
                      <p className="text-[#7D7D7D] text-sm mt-1">
                        Use within 90 days
                      </p>

                      <div className="mt-6">
                        <span className="text-3xl font-bold text-[#04082C]">
                          $5
                        </span>
                        <span className="text-[#6F6464] text-sm"> USD</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2: 180 Days */}
                <div
                  key="180"
                  className={`border rounded-xl p-5 cursor-pointer transition-all hover:shadow-md ${
                    durations === "180"
                      ? "border-[#2D65F2] bg-[#F5F9FF]"
                      : "border-[#DFDFDF] hover:border-gray-300"
                  }`}
                  onClick={(event) => {
                    if (event.target instanceof HTMLInputElement) return;
                    toggleDuration("180");
                  }}
                >
                  <div className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      value="180"
                      checked={durations === "180"}
                      onChange={() => toggleDuration("180")}
                      className="mt-1 accent-[#2D65F2]"
                    />
                    <div className="flex-1">
                      <p
                        className={`text-[18px] font-bold font-openSans ${
                          durations === "180"
                            ? "text-[#151515]"
                            : "text-[#6F6464]"
                        }`}
                      >
                        180 Days
                      </p>
                      <p className="text-[#7D7D7D] text-sm mt-1">
                        Use within 180 days
                      </p>

                      <div className="mt-6">
                        <span className="text-3xl font-bold text-[#04082C]">
                          $10
                        </span>
                        <span className="text-[#6F6464] text-sm"> USD</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 3: 1 Year */}
                <div
                  key="365"
                  className={`border rounded-xl p-5 cursor-pointer transition-all hover:shadow-md relative ${
                    durations === "365"
                      ? "border-[#2D65F2] bg-[#F5F9FF]"
                      : "border-[#DFDFDF] hover:border-gray-300"
                  }`}
                  onClick={(event) => {
                    if (event.target instanceof HTMLInputElement) return;
                    toggleDuration("365");
                  }}
                >
                  <div className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="radio"
                      value="365"
                      checked={durations === "365"}
                      onChange={() => toggleDuration("365")}
                      className="mt-1 accent-[#2D65F2]"
                    />
                    <div className="flex-1">
                      <p
                        className={`text-[18px] font-bold font-openSans ${
                          durations === "365"
                            ? "text-[#151515]"
                            : "text-[#6F6464]"
                        }`}
                      >
                        1 Year
                      </p>
                      <p className="text-[#7D7D7D] text-sm mt-1">
                        Use within 365 days
                      </p>

                      <div className="mt-6">
                        <span className="text-3xl font-bold text-[#04082C]">
                          $15
                        </span>
                        <span className="text-[#6F6464] text-sm"> USD</span>
                      </div>
                    </div>
                  </div>

                  {/* Popular Badge */}
                  {/* <div className="absolute -top-2 -right-2 bg-[#2D65F2] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Best Value
                  </div> */}
                </div>
              </div>
            </div>
            {/* Optional Driver License Verification */}
            <div className="border border-[#DDD] rounded-md p-5 mb-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-[#04082C]">
                      Driver Permit
                    </h3>
                    <Popover
                      placement="top"
                      trigger="hover"
                      overlayInnerStyle={{
                        padding: 0,
                        background: "transparent",
                        boxShadow: "none",
                      }}
                      content={
                        <div className="w-90">
                          <DriverPermitCardSkeleton />
                        </div>
                      }
                    >
                      <span className="inline-flex cursor-help">
                        <InfoIcon fill="#02636F" size={20} />
                      </span>
                    </Popover>
                  </div>
                  <p className="text-sm text-[#6F6464] leading-relaxed">
                    Get a dedicated verification URL in your portal to securely
                    process and validate the driver’s license.
                  </p>
                </div>

                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Switch
                    checked={licenseVerification}
                    onChange={toggleLicenseVerification}
                    className="scale-110"
                  />
                  {/* Show price only if it costs extra */}
                  {LICENSE_VERIFICATION_PRICE > 0 && (
                    <span className="text-3xl font-bold text-[#04082C] mt-1">
                      +${LICENSE_VERIFICATION_PRICE.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>

              {/* Optional expanded info when enabled */}
              {licenseVerification && (
                <div className="mt-4 pt-4 border-t border-[#DFDFDF]">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2D65F2] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#303030]">
                      After payment you will find a secure verification link
                      inside your DriveStamp portal to complete the driver
                      license check.
                    </p>
                  </div>
                </div>
              )}
            </div>
            {/* <div className="border border-[#DDD] rounded-md  p-3 mb-5">
              <button
                onClick={() => setShowCovered(!showCovered)}
                className="w-full flex items-center justify-between "
              >
                <h2 className="text-xl font-semibold text-[#04082C] mb-2">
                  Add extra protection
                </h2>

                {showCovered ? <ExpandedReportIcon /> : <CollapseIcon />}
              </button>
              {showCovered && (
                <div>
                  <div className="flex items-center justify-between my-5">
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={isIncluded}
                        onChange={handleToggle}
                        className="scale-110"
                      />
                      <div>
                        <h3 className="text-2xl font-bold text-[#04082C]">
                          Collision Damage Insurance (CDI)
                        </h3>
                        <p className="text-[#6F6464]">
                          Protect your rental vehicle
                        </p>
                      </div>
                    </div>
                    <div className="text-4xl font-bold text-[#04082C]">
                      ${dailyPrice}{" "}
                      <span className="text-base font-normal text-gray-500">
                        per day
                      </span>
                    </div>
                  </div>
                  <div className="px-6 pb-6 space-y-3 pt-2">
                    <h4 className="font-semibold text-[#04082C]">
                      What you will be covered for:
                    </h4>
                    {[
                      "All types of single & multiple vehicle accidents & vehicle repairs including body damage, windscreen, tyre, roof & underbody.",
                      "$0 deductible excess.",
                      "Key loss/replacement.",
                      "Expensive accident-related fees such as loss of use and administration fees.",
                      "Costs that are charged for damages to other vehicles (“third party” charges).",
                      "Provides coverage for theft, fire & vandalism.",
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <CheckCircle className="w-6 h-6 text-[#2D65F2] flex-shrink-0 mt-0.5" />
                        <span className="text-[#04082C] leading-relaxed">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="px-6 pb-6 space-y-3 pt-2">
                    <h4 className="font-semibold text-[#04082C]">
                      What you will not be covered for:
                    </h4>
                    {[
                      "Motorhome/RVs (vehicles that have a built-in toilet and shower), Campervans (vehicles with sleeping berths), 4x4s that are used on unsealed roads, Minibuses that require a non-standard drivers licence, Light Trucks.",
                      "If you are not covered for third party property and third party personal damages, please purchase it at the rental desk.",
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-[#6F6464] leading-relaxed">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div> */}
            {/* Insurance Toggle */}

            {/* <button className="flex items-center gap-2 text-[#2D65F2] font-medium hover:underline">
              <Info size={18} />
              Read Full Protection Terms
            </button> */}
          </div>

          {/* Right Column - Trip Summary & Payment */}
          <div className="lg:col-span-2 bg-white p-5 flex flex-col max-h-[500px] ">
            <h3 className="font-semibold text-lg mb-6 text-[#04082C]">
              Order Summary
            </h3>

            <div className="space-y-4 text-sm mb-10 text-[#303030]">
              <div className="flex justify-between">
                <span className="text-gray-500">Stamp Plan</span>
                <span className="font-medium">
                  {planName === "Single"
                    ? "Drive Log"
                    : planName === "Dual"
                      ? "Drive Audit"
                      : ""}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Stamp Access Durations</span>
                <span className="font-medium">
                  {formData.inspection?.access_limit_time || "30"} days
                </span>
              </div>
              {licenseVerification && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Driver Permit</span>
                  <span className="font-medium">
                    {LICENSE_VERIFICATION_PRICE > 0
                      ? `+$${LICENSE_VERIFICATION_PRICE.toFixed(2)}`
                      : "Included"}
                  </span>
                </div>
              )}
            </div>

            {/* Terms & Conditions Checkbox */}
            <div className="mb-6 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-0.5 w-5 h-5 accent-[#2D65F2] border-2 border-gray-300 rounded cursor-pointer 
                   "
                />
                <span className="text-sm text-gray-600 leading-relaxed">
                  I agree to the{" "}
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    className="text-[#04082C] hover:underline font-medium"
                  >
                    terms-of-service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    className="text-[#04082C] hover:underline font-medium"
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer group">
                <div>
                  <input
                    type="checkbox"
                    checked={agreeMarketing}
                    onChange={(e) => setAgreeMarketing(e.target.checked)}
                    className="mt-0.5 w-5 h-5 accent-[#2D65F2] border-2 border-gray-300 rounded cursor-pointer"
                  />
                </div>
                <span className="text-sm text-gray-600 leading-relaxed">
                  I agree to receive{" "}
                  <span className="text-[#04082C] font-medium">
                    Marketing Emails
                  </span>{" "}
                  from Drive Stamp.
                </span>
              </label>
            </div>

            {/* Price Box */}
            <div className="mt-auto bg-white rounded-md p-5 shadow-[0_2px_5px_0_rgba(0,0,0,0.05)]">
              <div className="text-right">
                {/* {isIncluded && (
                  <span className="text-gray-400 line-through block">
                    US ${originalTotal}
                  </span>
                )} */}
                <div className="text-[28px] font-bold text-[#04082C] mt-1">
                  US ${totalPrice ? totalPrice.toFixed(2) : "0"}
                </div>
                {/* {isIncluded && (
                  <p className="text-[#4DB429] font-medium mt-2">
                    You saved US ${(originalTotal - totalPrice).toFixed(2)} (
                    {Math.round(
                      ((originalTotal - totalPrice) / originalTotal) * 100,
                    )}
                    %)
                  </p>
                )} */}
              </div>

              {/* Proceed Button - Disabled until checkbox is checked */}
              <button
                onClick={handleSubmit(onSubmit, (errors) => {
                  console.log("Validation Errors");
                  console.log(errors);
                })}
                disabled={!agreeTerms}
                className={`w-full h-12 font-bold rounded-md transition-all duration-200 cursor-pointer mt-5
        ${
          agreeTerms
            ? "submit-button"
            : "bg-[#DDD] text-gray-500 cursor-not-allowed"
        }`}
              >
                {isLoading ? <LoadingButtonAnimation /> : "PROCEED TO PAYMENT"}
              </button>

              {/* <buttoncle
                onClick={() => setEditDurations(true)}
                className="w-full border capitalize border-[#ddd] text-[#04082C] font-semibold py-4 rounded-md mt-3 hover:bg-gray-50 transition-colors"
              >
                Edit Durations
              </button> */}
            </div>
          </div>
        </div>
      </div>
      <FBModal
        openModal={editDurations}
        onCancel={() => setEditDurations(false)}
        closeIcon
        zIndex={2000}
        // center
        top={"300px"}
        blur
      >
        <CustomDateRangeSelector
          onChange={handleDateChange}
          initialStartDate={formData.trip?.startDate} // Pass existing value
          initialEndDate={formData.trip?.endDate}
        />
      </FBModal>
    </div>
  );
}
